import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Lock } from "lucide-react";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";

/** Blocks pleadings/documents/orders until the confidentiality agreement is digitally accepted. */
export const ConfidentialityGate = ({ caseId, accepted, onAccepted, children }) => {
  const [agreement, setAgreement] = useState(null);
  const [tick, setTick] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!accepted) api.get(`/cases/${caseId}/confidentiality`).then(({ data }) => setAgreement(data)).catch(() => {});
  }, [caseId, accepted]);

  if (accepted) return children;

  const accept = async () => {
    setBusy(true);
    try {
      await api.post(`/cases/${caseId}/confidentiality/accept`);
      toast.success("Confidentiality agreement accepted — recorded in the audit trail");
      onAccepted();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-2 border-blue-900 bg-card" data-testid="confidentiality-gate">
      <div className="px-6 py-4 border-b border-border flex items-center gap-2">
        <Lock className="h-4 w-4 text-blue-900 dark:text-blue-400" />
        <p className="label-caps">Confidentiality agreement · acceptance required</p>
      </div>
      <div className="px-6 py-5">
        <p className="text-sm text-muted-foreground">
          The pleadings, documents, orders and awards of this reference open only after you digitally accept the
          confidentiality terms below. Your acceptance, with date and time, is entered in the case audit trail.
        </p>
        <pre className="mt-5 whitespace-pre-wrap font-sans text-sm leading-relaxed border border-border bg-muted/40 p-5 max-h-80 overflow-y-auto"
          data-testid="confidentiality-text">
          {agreement ? agreement.text : "Loading agreement…"}
        </pre>
        <label className="mt-5 flex items-start gap-3 text-sm cursor-pointer">
          <input type="checkbox" checked={tick} onChange={(e) => setTick(e.target.checked)} className="mt-1 h-4 w-4 accent-blue-900"
            data-testid="confidentiality-checkbox" />
          <span>I have read the confidentiality agreement and accept its terms as a digital signature on my behalf.</span>
        </label>
        <Button disabled={!tick || busy || !agreement} onClick={accept} data-testid="confidentiality-accept-button"
          className="mt-5 rounded-none bg-blue-900 hover:bg-blue-800 text-white uppercase tracking-widest text-xs h-11 px-6">
          <ShieldCheck className="h-4 w-4 mr-2" /> {busy ? "Recording…" : "Accept & sign"}
        </Button>
        {agreement && (
          <p className="text-[11px] text-muted-foreground mt-3" data-testid="confidentiality-meta">
            Version {agreement.version} · generated {fmtDateTime(agreement.generated_at)}
          </p>
        )}
      </div>
    </div>
  );
};

/** Compact list of who has signed, for the tribunal. */
export const ConfidentialityStatus = ({ caseId }) => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/cases/${caseId}/confidentiality`).then(({ data }) => setData(data)).catch(() => {}); }, [caseId]);
  if (!data) return null;
  return (
    <div className="border border-border bg-card p-4 text-sm" data-testid="confidentiality-status">
      <p className="label-caps">Confidentiality agreement · v{data.version}</p>
      {data.acceptances.length === 0 ? (
        <p className="mt-1 text-muted-foreground">No participant has signed yet.</p>
      ) : (
        <ul className="mt-2 space-y-1">
          {data.acceptances.map((a, i) => (
            <li key={i} className="text-xs" data-testid={`confidentiality-signed-${i}`}>
              <span className="font-medium">{a.user_name}</span> ({a.role}) · {fmtDateTime(a.accepted_at)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
